import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BusyBeeLogo from '../components/BusyBeeLogo';
import { Download, ArrowRight, FileText, Image as ImageIcon, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';
import { monthAndYear } from '../lib/siteDates';

// The words of the releases, colors and screenshots live in public/locales/<lang>/landing.json
// under press.releases / press.colors / press.screenshot_alts; this keeps dates, hex codes and files.
const pressReleases = [
  // The Gmail integration is hidden for now (GOOGLE_APP_INTEGRATIONS).
  ...(GOOGLE_APP_INTEGRATIONS ? [{ key: 'documents_gmail', month: '2026-03' }] : []),
  { key: 'teams', month: '2026-02' },
  { key: 'webhooks', month: '2026-01' },
  { key: 'launch', month: '2025-12' },
];

// An "In the News" block used to live here with quotes attributed to Product
// Hunt, Indie Hackers and Hacker News. None of those outlets ever published
// them. Putting words in a named third party's mouth is not a grey area, so
// the data and its section are gone until there is real coverage to link to.

const brandColors = [
  { key: 'honey', hex: '#F59E0B' },
  { key: 'honey_light', hex: '#FCD34D' },
  { key: 'ink', hex: '#0A0A0B' },
  { key: 'slate', hex: '#27272A' },
  { key: 'mute', hex: '#A1A1AA' },
];

const screenshotShowcase = [
  { key: 'home', src: '/screenshots/home/main.png' },
  { key: 'tasks_calendar', src: '/screenshots/tasks/calendar.png' },
  { key: 'chats', src: '/screenshots/bots/chat.png' },
  { key: 'board', src: '/screenshots/projects/kanban.png' },
  { key: 'timeline', src: '/screenshots/projects/timeline.png' },
  { key: 'whiteboard', src: '/screenshots/documents/whiteboard.png' },
];

const asList = (v) => (Array.isArray(v) ? v : []);

const PressPage = () => {
  const { t, i18n } = useTranslation('landing');
  const lang = i18n.resolvedLanguage || 'en';
  const [openRelease, setOpenRelease] = useState(null);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO title={t('press.seo.title')} description={t('press.seo.description')} canonical="/press" />
      <Navbar />

      <main className="pt-20 pb-24">
        <section className="max-w-4xl mx-auto px-4 py-12 sm:py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{t('press.headline')}</h1>
          <p className="text-xl text-gray-400">{t('press.subtitle')}</p>
        </section>

        {/* Brand Assets */}
        <section className="max-w-4xl mx-auto px-4 py-8 sm:py-12" id="brand-assets">
          <h2 className="text-2xl font-bold text-white mb-6">{t('press.brand_assets.headline')}</h2>
          <div className="bg-white/5 rounded-2xl border border-gray-800 p-6 sm:p-8">
            <div className="flex items-center gap-4 mb-6">
              <BusyBeeLogo className="h-12" />
            </div>
            <p className="text-gray-400 mb-6">
              {t('press.brand_assets.body')}
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/press-kit/busybee-logo.svg"
                download
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium hover:bg-amber-400 transition-colors"
              >
                <Download className="w-4 h-4" />
                {t('press.brand_assets.download_logo')}
              </a>
              <a
                href="#brand-guidelines"
                onClick={scrollTo('brand-guidelines')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-700 text-white rounded-lg font-medium hover:bg-white/5 transition-colors"
              >
                <FileText className="w-4 h-4" />
                {t('press.brand_assets.guidelines_link')}
              </a>
              <a
                href="#screenshots"
                onClick={scrollTo('screenshots')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-700 text-white rounded-lg font-medium hover:bg-white/5 transition-colors"
              >
                <ImageIcon className="w-4 h-4" />
                {t('press.brand_assets.screenshots_link')}
              </a>
            </div>
          </div>
        </section>

        {/* Press Releases */}
        <section className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <h2 className="text-2xl font-bold text-white mb-6">{t('press.releases_headline')}</h2>
          <div className="space-y-4">
            {pressReleases.map(({ key, month }, i) => {
              const expanded = openRelease === i;
              const release = {
                title: t(`press.releases.${key}.title`),
                date: monthAndYear(month, lang),
                summary: t(`press.releases.${key}.summary`),
                body: asList(t(`press.releases.${key}.body`, { returnObjects: true })),
              };
              return (
                <div key={i} className="bg-white/5 rounded-xl border border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenRelease(expanded ? null : i)}
                    aria-expanded={expanded}
                    className="w-full flex items-start justify-between gap-4 p-6 text-left hover:bg-white/[0.03] transition-colors group"
                  >
                    <div className="min-w-0">
                      <h3 className="text-white font-semibold group-hover:text-amber-400 transition-colors mb-1">{release.title}</h3>
                      <p className="text-gray-500 text-sm">{release.date}</p>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded && (
                    <div className="px-6 pb-6 border-t border-gray-800 pt-5">
                      <p className="text-amber-400/80 text-sm font-medium mb-4">{release.summary}</p>
                      <div className="space-y-4 text-gray-300 leading-relaxed">
                        {release.body.map((p, k) => <p key={k}>{p}</p>)}
                      </div>
                      <a
                        href={'/contact'}
                        className="inline-flex items-center gap-2 mt-5 text-amber-400 hover:text-amber-300 font-medium text-sm"
                      >
                        {t('press.request_context')}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Brand Guidelines */}
        <section className="max-w-4xl mx-auto px-4 py-8 sm:py-12 scroll-mt-24" id="brand-guidelines">
          <h2 className="text-2xl font-bold text-white mb-6">{t('press.brand_assets.guidelines_link')}</h2>
          <div className="bg-white/5 rounded-2xl border border-gray-800 p-6 sm:p-8 space-y-8">
            <div>
              <h3 className="text-white font-semibold mb-3">{t('press.guidelines.naming.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                <Trans t={t} ns="landing" i18nKey="press.guidelines.naming.text" components={{ b: <span className="text-white font-medium" />, domain: <span className="text-amber-400" /> }} />
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-3">{t('press.guidelines.palette.title')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {brandColors.map((c) => (
                  <div key={c.hex} className="rounded-lg border border-gray-800 overflow-hidden">
                    <div className="h-16" style={{ backgroundColor: c.hex }} />
                    <div className="p-3">
                      <div className="text-white text-sm font-medium">{t(`press.colors.${c.key}.name`)}</div>
                      <div className="text-gray-500 text-xs font-mono mt-0.5">{c.hex}</div>
                      <div className="text-gray-500 text-xs mt-1">{t(`press.colors.${c.key}.usage`)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-3">{t('press.guidelines.typography.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('press.guidelines.typography.text')}
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-3">{t('press.guidelines.voice.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                <Trans t={t} ns="landing" i18nKey="press.guidelines.voice.text" components={{ em: <em /> }} />
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-3">{t('press.guidelines.logo.title')}</h3>
              <ul className="text-gray-400 leading-relaxed space-y-2 list-disc list-inside">
                {asList(t('press.guidelines.logo.items', { returnObjects: true })).map((rule, i) => <li key={i}>{rule}</li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* Screenshots */}
        <section className="max-w-6xl mx-auto px-4 py-8 sm:py-12 scroll-mt-24" id="screenshots">
          <h2 className="text-2xl font-bold text-white mb-6">{t('press.screenshots.headline')}</h2>
          <p className="text-gray-400 mb-6">{t('press.screenshots.body')}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {screenshotShowcase.map((s) => (
              <a
                key={s.src}
                href={s.src}
                target="_blank"
                rel="noreferrer"
                className="block rounded-xl border border-gray-800 overflow-hidden bg-white/5 hover:border-amber-500/30 transition-colors"
              >
                <img src={s.src} alt={t(`press.screenshot_alts.${s.key}`)} className="w-full aspect-video object-cover object-top" loading="lazy" />
                <div className="p-3 text-gray-400 text-sm flex items-center justify-between">
                  <span>{t(`press.screenshot_alts.${s.key}`)}</span>
                  <Download className="w-4 h-4" />
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 rounded-2xl border border-gray-800 p-6 sm:p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">{t('press.inquiries.headline')}</h2>
            <p className="text-gray-400 mb-6">{t('press.inquiries.body')}</p>
            <Link to="/contact" className="text-amber-400 hover:text-amber-300 font-medium">busybee.day/contact</Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PressPage;
